import { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { tokens } from '../tokens';
import { useBreakpoint } from '../hooks/useBreakpoint';

/* ─── Replace with your real Supabase project URL once the
       "popularservices" storage bucket/folder is created.
       Each file must be named exactly like the service title below,
       e.g. "Bodaboda Couriers.png" ─── */
const SUPABASE_URL = "https://YOUR-PROJECT-REF.supabase.co/storage/v1/object/public/popularservices";

/* ─── Design tokens local to this section ─── */
const C = {
  green:      "#1e9254",
  greenDark:  "#155c36",
  greenDeep:  "#0d2818", // matches hero background
  greenTint:  "#eef8f3",
  greenBorder:"#a7d9bc",
  text:       "#0f1117",
  sub:        "#4a5268",
  border:     "#E5E7EB",
  white:      "#FFFFFF",
  bg:         "#F7F8FA", // matches Top Rated JiraniPros section background
  serif:      "'Instrument Serif', Georgia, serif",
  sans:       tokens?.font?.family?.sans || "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  tx:         "all 0.25s cubic-bezier(.4,0,.2,1)",
};

/* ─── Real, substantive one-to-two sentence descriptions. No em dashes. ─── */
const SERVICES = [
  { title: "Bodaboda Couriers", slug: "bodaboda-couriers",
    desc: "Need something across town in a hurry? Verified riders pick up and deliver parcels, documents, and food across the city, tracked from pickup to drop-off." },
  { title: "Home Cleaning & Laundry", slug: "cleaning",
    desc: "From a quick tidy-up to a full deep clean, vetted cleaners handle floors, laundry, and everything in between, so you come home to a spotless space." },
  { title: "Plumbing Repairs", slug: "plumbing",
    desc: "Leaking taps, blocked drains, burst pipes: licensed plumbers arrive fast, diagnose the issue on the spot, and fix it right the first time." },
  { title: "Electronics Repair", slug: "electronics-repair",
    desc: "TVs, sound systems, and home electronics fixed at your door. Technicians diagnose the fault and get your gadgets working again, often same day." },
  { title: "Hair & Beauty", slug: "hair-beauty",
    desc: "Skilled stylists and beauty pros come to you for cuts, braids, makeup, and more, delivering salon-quality results without leaving your home." },
  { title: "Home Tutoring", slug: "home-tutoring",
    desc: "One-on-one tutoring in the subjects that matter most, matched to your child's level and pace, with sessions held right in your home." },
  { title: "Photography", slug: "photography",
    desc: "Professional photographers for family portraits, events, and personal shoots, capturing high-quality images edited and delivered fast." },
  { title: "Electrical Repairs", slug: "electrical",
    desc: "Faulty wiring, tripped breakers, new installations: certified electricians handle it safely, with every job inspected before they leave." },
  { title: "Gardening & Landscaping", slug: "gardening",
    desc: "From regular lawn care to full landscape makeovers, gardeners keep your outdoor space healthy, tidy, and looking its best year-round." },
  { title: "Carpentry & Furniture", slug: "carpentry-furniture",
    desc: "Custom furniture, repairs, and fittings built by skilled carpenters, with quality craftsmanship measured, made, and installed to fit your space exactly." },
  { title: "Moving & Relocation", slug: "moving",
    desc: "Packing, loading, transport, and unloading: moving crews handle the heavy lifting so your move to a new place is smooth and stress-free." },
  { title: "Babysitting & Childcare", slug: "childcare",
    desc: "Background-checked, experienced caregivers you can trust with your little ones, available for a few hours or a regular weekly schedule." },
  { title: "Fitness Coaching", slug: "fitness",
    desc: "Personal trainers who come to you, build a plan around your goals, and keep you accountable, with no gym membership required." },
  { title: "Auto Repair", slug: "auto-repair",
    desc: "From routine servicing to breakdown repairs, mobile mechanics come to your location and get your car back on the road quickly." },
  { title: "Errands & Delivery", slug: "errands-delivery",
    desc: "Grocery runs, bill payments, pickups and drop-offs: reliable runners handle your errands so your to-do list actually gets shorter." },
].map((s) => ({ ...s, image: `${SUPABASE_URL}/${s.title}.png` }));

/* ─── Wave rhythm for where the photo sits inside each card's image
       area: bottom-left, top-left, top-right, bottom-center,
       bottom-right, then repeats. Absolute-positioned with a small
       fixed gap so the photo sits right up against its anchored
       edge(s) — not floated via percentages, which is what caused
       every image to collapse to the same spot last round. ─── */
const GAP = 8;
const POSITIONS = [
  { left: GAP,  bottom: GAP },                                          // bottom-left
  { left: GAP,  top: GAP },                                             // top-left
  { right: GAP, top: GAP },                                             // top-right
  { left: "50%", bottom: GAP, transform: "translateX(-50%)" },          // bottom-center
  { right: GAP, bottom: GAP },                                          // bottom-right
];

/* ─── Individual card — every card is the same fixed height. Text
       always sits in a fixed block up top; the photo is absolutely
       positioned inside the remaining area at its wave position. ─── */
function ServiceCard({ service, pos }) {
  const [hovered, setHovered] = useState(false);
  const [imgErr, setImgErr] = useState(false);
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  const CARD_W = isMobile ? 268 : 320;
  const CARD_H = isMobile ? 440 : 500;
  const TEXT_H = isMobile ? 172 : 190;
  const isCenter = pos.left === "50%";
  const IMG_W = Math.round(CARD_W * (isCenter ? 0.72 : 0.84));
  const IMG_H = Math.round((CARD_H - TEXT_H) * 0.86);

  return (
    <div
      onClick={() => navigate(`/services/${service.slug}`)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flexShrink: 0,
        width: CARD_W,
        height: CARD_H,
        borderRadius: 18,
        overflow: "hidden",
        position: "relative",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        background: C.greenDeep,
        transform: hovered ? "translateY(-6px)" : "translateY(0)",
        boxShadow: hovered
          ? "0 24px 48px rgba(13,40,24,.26), 0 6px 16px rgba(13,40,24,.14)"
          : "0 3px 14px rgba(13,40,24,.10), 0 1px 3px rgba(13,40,24,.06)",
        transition: C.tx,
      }}
    >
      {/* ── Text block — always fixed, top ── */}
      <div style={{
        height: TEXT_H,
        flexShrink: 0,
        padding: "26px 24px 18px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
      }}>
        <div style={{
          fontFamily: C.serif,
          fontStyle: "italic",
          fontSize: isMobile ? 21 : 24,
          fontWeight: 400,
          color: "#fff",
          lineHeight: 1.16,
          letterSpacing: "-0.01em",
          marginBottom: 10,
        }}>
          {service.title}
        </div>
        <div style={{
          fontFamily: C.sans,
          fontSize: isMobile ? 13.5 : 14.5,
          fontWeight: 600,
          color: "rgba(255,255,255,.86)",
          lineHeight: 1.5,
          letterSpacing: "-0.005em",
        }}>
          {service.desc}
        </div>
      </div>

      {/* ── Image area — fills the rest of the card; photo is
             absolutely positioned inside it at its wave corner ── */}
      <div style={{ flex: 1, position: "relative" }}>
        {!imgErr ? (
          <img
            src={service.image}
            alt={service.title}
            onError={() => setImgErr(true)}
            style={{
              position: "absolute",
              width: IMG_W,
              height: IMG_H,
              objectFit: "cover",
              display: "block",
              borderRadius: 14,
              transform: hovered
                ? `${pos.transform ? pos.transform + " " : ""}scale(1.04)`
                : pos.transform || "none",
              transition: "transform 0.5s cubic-bezier(.4,0,.2,1)",
              ...pos,
            }}
          />
        ) : (
          <div style={{
            position: "absolute",
            width: IMG_W,
            height: IMG_H,
            borderRadius: 14,
            background: `linear-gradient(160deg, ${C.greenDark} 0%, ${C.green} 100%)`,
            ...pos,
          }}/>
        )}
      </div>
    </div>
  );
}

/* ─── Scroll arrow button ─── */
function Arrow({ dir, enabled, onClick }) {
  const [h, setH] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={!enabled}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      aria-label={dir === 1 ? "Scroll right" : "Scroll left"}
      style={{
        width: 34, height: 34, borderRadius: "50%",
        border: `1.5px solid ${enabled && h ? C.greenBorder : C.border}`,
        background: enabled && h ? C.greenTint : C.white,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: enabled ? "pointer" : "default",
        opacity: enabled ? 1 : 0.32,
        transition: C.tx, flexShrink: 0,
        boxShadow: enabled ? "0 2px 8px rgba(0,0,0,.07)" : "none",
      }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
        stroke={enabled && h ? C.green : C.sub}
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        {dir === 1
          ? <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>
          : <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>
        }
      </svg>
    </button>
  );
}

/* ─── Main section ─── */
export default function PopularServices() {
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);
  const { isMobile, isTablet } = useBreakpoint();

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    updateArrows();
    return () => el.removeEventListener("scroll", updateArrows);
  }, [updateArrows]);

  const padX = isMobile ? 16 : isTablet ? 20 : 32;

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({
      left: dir * (isMobile ? 300 : 340),
      behavior: "smooth",
    });
  };

  return (
    <section style={{
      margin: 0,
      padding: isMobile ? "16px 0 16px" : "20px 0 24px",
      fontFamily: C.sans,
      background: C.bg,
      width: "100%",
      overflow: "hidden",
    }}>
      <style>{`
        .ps-track::-webkit-scrollbar { display: none }
        .ps-track { -ms-overflow-style: none; scrollbar-width: none }
      `}</style>

      {/* ── Header — same padX as the track below, no centering, so
             the two can never drift out of alignment ── */}
      <div style={{ width: "100%", padding: `0 ${padX}px` }}>
        <h2 style={{
          fontFamily: C.serif,
          fontSize: "clamp(28px, 4vw, 38px)",
          fontWeight: 400,
          color: C.text,
          margin: 0,
          lineHeight: 1.08,
          letterSpacing: "-0.03em",
          marginBottom: isMobile ? 22 : 28,
        }}>
          Everything you need,{" "}
          <em style={{ fontStyle: "italic", color: C.green }}>right next door</em>
        </h2>
      </div>

      {/* ── Cards strip — same padX as the header above (plain, no
             maxWidth cap), so the left edge is pixel-identical to the
             header no matter the viewport. Right side is uncapped and
             bleeds freely, so wide screens don't leave a dead gap. ── */}
      <div
        ref={scrollRef}
        className="ps-track"
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 22,
          overflowX: "auto",
          paddingBottom: 12,
          paddingTop: 6,
          paddingLeft: padX,
          paddingRight: padX,
          scrollSnapType: "x mandatory",
          width: "100%",
        }}
      >
        {SERVICES.map((service, i) => (
          <div key={service.slug} style={{ scrollSnapAlign: "start", flexShrink: 0 }}>
            <ServiceCard service={service} pos={POSITIONS[i % POSITIONS.length]} />
          </div>
        ))}
      </div>

      {/* ── Arrows — same padX, full width, no maxWidth cap ── */}
      {!isMobile && (
        <div style={{ width: "100%", padding: `0 ${padX}px` }}>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Arrow dir={-1} enabled={canLeft} onClick={() => scroll(-1)}/>
            <Arrow dir={1} enabled={canRight} onClick={() => scroll(1)}/>
          </div>
        </div>
      )}
    </section>
  );
}